// Canonical "save a document into a silo" dialog (Jul 2026, Randy).
// Used by the Doc Editor now; Workbench/Refinery saves route through it next.
//
// Rules baked in:
//  • Same silo   → default OVERRIDE this file (radio: Override / Save as new copy).
//  • Other silo  → default SAVE AS NEW there, with an optional "also update the
//                  original" toggle.
//  • Duplicate name guard: before creating a NEW file (or renaming into a name
//    another file owns), we check the destination silo. On collision the user
//    must Rename or Overwrite the same-named file. Match ignores case + spacing.
import { useEffect, useMemo, useState } from "react";
import { X, Anchor, BookOpen, Books, Warning, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const TIERS = [
  { id: 1, label: "Tier 1 · Anchor", icon: Anchor, desc: "Always-active soul. Recommended 100k–150k chars." },
  { id: 2, label: "Tier 2 · Logic Core", icon: BookOpen, desc: "Heavy-weighted primary library. Searched first." },
  { id: 3, label: "Tier 3 · Library", icon: Books, desc: "Raw archive — consulted only if Tiers 1 & 2 can't answer." },
];

const norm = (s) => (s || "").trim().replace(/\s+/g, " ").toLowerCase();

export default function DocSaveDialog({ open, onClose, content, sourceSiloId, sourceDoc = null, silos = [], onSaved,
  lockSilo = false, defaultTitle = "", initialTier = 2, checkOversize, onOversize, dialogTitle,
  sourceSurface = null, sourceOperation = null }) {
  const commitMode = !sourceDoc; // no source doc = a fresh save from staging (Workbench/Refinery)
  const [destSiloId, setDestSiloId] = useState(sourceSiloId);
  const [tier, setTier] = useState(sourceDoc?.tier || 3);
  const [title, setTitle] = useState(sourceDoc?.title || "");
  const [hidden, setHidden] = useState(sourceDoc?.enabled === false);
  const [sameSiloMode, setSameSiloMode] = useState("override"); // override | new
  const [alsoUpdateOriginal, setAlsoUpdateOriginal] = useState(false);
  const [conflict, setConflict] = useState(null); // { doc_id, title, renameOnly? }
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDestSiloId(sourceSiloId);
    if (commitMode) {
      setTier(initialTier || 2);
      setTitle(defaultTitle || "");
      setHidden(false);
    } else {
      setTier(sourceDoc?.tier || 3);
      setTitle(sourceDoc?.title || "");
      setHidden(sourceDoc?.enabled === false);
    }
    setSameSiloMode("override");
    setAlsoUpdateOriginal(false);
    setConflict(null);
    setBusy(false);
  }, [open, sourceSiloId, sourceDoc, commitMode, defaultTitle, initialTier]);

  const isSameSilo = destSiloId === sourceSiloId;
  const destName = useMemo(() => silos.find((s) => s.id === destSiloId)?.name || "this silo", [silos, destSiloId]);
  const sourceName = useMemo(() => silos.find((s) => s.id === sourceSiloId)?.name || "the original silo", [silos, sourceSiloId]);
  const bytes = useMemo(() => new Blob([content || ""]).size, [content]);
  const perDocKB = { 1: 300, 2: 1000, 3: 5000 }[tier];
  const oversize = bytes > perDocKB * 1000;

  if (!open) return null;

  const finish = (msg) => { toast.success(msg); setBusy(false); setConflict(null); onSaved?.(); onClose?.(); };

  const doSave = async (forceOverwriteDocId = null) => {
    const t = title.trim();
    if (!t) return toast.error("Give the file a name.");
    if (!destSiloId) return toast.error("Choose a destination silo.");
    setBusy(true);
    // Provenance to stamp on a NEWLY created file: commit mode uses the caller's
    // surface (Workbench/Refinery); an editor "save as new copy" inherits the
    // source file's origin so a copy keeps its Workbench/Refinery tag.
    const provSurface = commitMode ? (sourceSurface || null) : (sourceDoc?.source_surface || null);
    const provOperation = commitMode ? (sourceOperation || null) : (sourceDoc?.source_operation || null);
    try {
      // ── Commit mode: fresh save from staging (Workbench/Refinery) ──
      if (commitMode) {
        // Oversize for this tier → hand off to Split & Push (caller decides).
        if (!forceOverwriteDocId && checkOversize?.(content, tier)) {
          onOversize?.({ title: t, tier, hidden, siloId: destSiloId });
          setBusy(false); onClose?.(); return;
        }
        let targetId = forceOverwriteDocId;
        if (!targetId) {
          const { data } = await api.get(`/silos/${destSiloId}/docs/name-exists`, { params: { title: t } });
          if (data.exists) { setConflict({ doc_id: data.doc_id, title: data.title }); setBusy(false); return; }
        }
        if (targetId) {
          await api.patch(`/silos/${destSiloId}/docs/${targetId}/content`, { content, title: t, tier, enabled: !hidden });
        } else {
          const { data: created } = await api.post(`/silos/${destSiloId}/docs/paste`, { title: t, content, tier, source_surface: provSurface, source_operation: provOperation });
          if (hidden && created?.id) { try { await api.patch(`/silos/${destSiloId}/docs/${created.id}/enabled`, { enabled: false }); } catch { /* non-fatal */ } }
        }
        return finish(targetId ? "Committed — existing file overwritten." : `Committed to “${destName}” · Tier ${tier}.`);
      }
      // ── In-place overwrite of THIS file ──
      if (isSameSilo && sameSiloMode === "override" && !forceOverwriteDocId) {
        if (norm(t) !== norm(sourceDoc.title)) {
          const { data } = await api.get(`/silos/${destSiloId}/docs/name-exists`, { params: { title: t, exclude_id: sourceDoc.id } });
          if (data.exists) { setConflict({ doc_id: data.doc_id, title: data.title, renameOnly: true }); setBusy(false); return; }
        }
        await api.patch(`/silos/${destSiloId}/docs/${sourceDoc.id}/content`, { content, title: t, tier, enabled: !hidden });
        return finish("Saved — file updated.");
      }
      // ── New file (same-silo copy OR different silo), or forced overwrite of a same-named file ──
      let targetId = forceOverwriteDocId;
      if (!targetId) {
        const { data } = await api.get(`/silos/${destSiloId}/docs/name-exists`, { params: { title: t } });
        if (data.exists) { setConflict({ doc_id: data.doc_id, title: data.title }); setBusy(false); return; }
      }
      if (targetId) {
        await api.patch(`/silos/${destSiloId}/docs/${targetId}/content`, { content, title: t, tier, enabled: !hidden });
      } else {
        const { data: created } = await api.post(`/silos/${destSiloId}/docs/paste`, { title: t, content, tier, source_surface: provSurface, source_operation: provOperation });
        if (hidden && created?.id) {
          try { await api.patch(`/silos/${destSiloId}/docs/${created.id}/enabled`, { enabled: false }); } catch { /* non-fatal */ }
        }
      }
      if (!isSameSilo && alsoUpdateOriginal) {
        try { await api.patch(`/silos/${sourceSiloId}/docs/${sourceDoc.id}/content`, { content }); }
        catch { toast.warning("Saved the new file, but could NOT update the original — open it and save again."); }
      }
      return finish(targetId ? "Saved — existing file overwritten." : `Saved as a new file in ${destName}.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
      setBusy(false);
    }
  };

  const submitLabel = commitMode ? "Commit" : (isSameSilo && sameSiloMode === "override" ? "Save (overwrite)" : "Save as new");

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="doc-save-dialog"
      style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom, 0px))" }}>
      <div className="absolute inset-0 bg-[#1C1B1A]/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg bg-white border hairline rounded-xl shadow-2xl overflow-hidden flex flex-col"
        style={{ maxHeight: "calc(100dvh - 2rem - env(safe-area-inset-bottom, 0px))" }}>
        <div className="flex items-center justify-between px-6 py-4 border-b hairline shrink-0">
          <div className="font-serif-display text-lg text-[#1C1B1A]">{dialogTitle || "Save document"}</div>
          <button type="button" onClick={onClose} className="p-2 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE]"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-5 flex-1 min-h-0 overflow-y-auto">
          {/* Destination silo */}
          <div>
            <label className="block text-sm text-[#6B6A68] mb-1.5">Destination silo</label>
            {commitMode && lockSilo ? (
              <div className="w-full bg-[#F0F1EE] border hairline rounded-md px-4 py-3 text-sm text-[#1C1B1A]" data-testid="doc-save-silo-locked">{destName}</div>
            ) : (
              <select data-testid="doc-save-silo" value={destSiloId} onChange={(e) => { setDestSiloId(e.target.value); setConflict(null); }}
                className="w-full bg-white border hairline rounded-md px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]">
                {silos.map((s) => <option key={s.id} value={s.id}>{s.name}{s.id === sourceSiloId ? " (current)" : ""}</option>)}
              </select>
            )}
          </div>

          {/* Mode: same silo → override/new radios; other silo → new + update-original */}
          {!commitMode && (isSameSilo ? (
            <div className="space-y-2" data-testid="doc-save-mode">
              <label className={`flex gap-3 p-3 rounded-md border cursor-pointer ${sameSiloMode === "override" ? "border-[#4A5D4E] bg-[#F0F1EE]" : "hairline hover:border-[#4A5D4E]/50"}`} data-testid="doc-save-mode-override">
                <input type="radio" name="savemode" checked={sameSiloMode === "override"} onChange={() => { setSameSiloMode("override"); setConflict(null); }} className="mt-1" />
                <div className="text-xs leading-relaxed">
                  <div className="text-[#1C1B1A] font-medium text-sm">Override this file</div>
                  <div className="text-[#6B6A68] mt-0.5">Replaces the existing file's contents in place.</div>
                </div>
              </label>
              <label className={`flex gap-3 p-3 rounded-md border cursor-pointer ${sameSiloMode === "new" ? "border-[#4A5D4E] bg-[#F0F1EE]" : "hairline hover:border-[#4A5D4E]/50"}`} data-testid="doc-save-mode-new">
                <input type="radio" name="savemode" checked={sameSiloMode === "new"} onChange={() => { setSameSiloMode("new"); setConflict(null); }} className="mt-1" />
                <div className="text-xs leading-relaxed">
                  <div className="text-[#1C1B1A] font-medium text-sm">Save as a new copy</div>
                  <div className="text-[#6B6A68] mt-0.5">Keeps the original and adds a separate new file.</div>
                </div>
              </label>
            </div>
          ) : (
            <div className="p-3 rounded-md border hairline bg-[#FDFCF8] space-y-2" data-testid="doc-save-crosssilo">
              <div className="text-xs text-[#6B6A68]">Saves as a <span className="text-[#1C1B1A] font-medium">new file</span> in “{destName}”.</div>
              <label className="flex items-start gap-2 cursor-pointer" data-testid="doc-save-update-original">
                <input type="checkbox" className="mt-0.5 accent-[#4A5D4E]" checked={alsoUpdateOriginal} onChange={(e) => setAlsoUpdateOriginal(e.target.checked)} />
                <span className="text-xs text-[#6B6A68]">Also apply these edits to the original file in “{sourceName}”.</span>
              </label>
            </div>
          ))}

          {/* Title */}
          <div>
            <label className="block text-sm text-[#6B6A68] mb-1.5">File name</label>
            <input data-testid="doc-save-title" value={title} onChange={(e) => { setTitle(e.target.value); if (conflict) setConflict(null); }}
              className="w-full bg-white border hairline rounded-md px-4 py-3 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
              placeholder="File name" />
          </div>

          {/* Duplicate-name conflict banner */}
          {conflict && (
            <div className="flex flex-col gap-2 text-xs text-[#C16E5A] bg-[#FDF1ED] border border-[#F2D0C5] rounded-md px-3 py-2.5" data-testid="doc-save-conflict">
              <div className="flex gap-2 items-start">
                <Warning size={14} weight="duotone" className="mt-0.5 shrink-0" />
                <div>
                  A file named <strong>“{conflict.title}”</strong> already exists in “{destName}”.
                  {conflict.renameOnly ? " Choose a different name to continue." : " Rename it below, or overwrite that file."}
                </div>
              </div>
              {!conflict.renameOnly && (
                <button type="button" onClick={() => doSave(conflict.doc_id)} disabled={busy} data-testid="doc-save-conflict-overwrite"
                  className="self-start px-3 py-1.5 rounded-md bg-[#C16E5A] text-white hover:bg-[#A85942] disabled:opacity-50">
                  Overwrite “{conflict.title}”
                </button>
              )}
            </div>
          )}

          {/* Tier */}
          <div>
            <div className="block text-sm text-[#6B6A68] mb-2">Tier</div>
            <div className="space-y-2">
              {TIERS.map((t) => {
                const Icon = t.icon; const active = tier === t.id;
                return (
                  <label key={t.id} className={`flex gap-3 p-3 rounded-md border cursor-pointer ${active ? "border-[#4A5D4E] bg-[#F0F1EE]" : "hairline hover:border-[#4A5D4E]/50"}`} data-testid={`doc-save-tier-${t.id}`}>
                    <input type="radio" name="savetier" checked={active} onChange={() => setTier(t.id)} className="mt-1.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-[#1C1B1A] text-sm font-medium"><Icon size={16} weight="duotone" /> {t.label}</div>
                      <div className="text-xs text-[#6B6A68] mt-1 leading-relaxed">{t.desc}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Hide toggle */}
          <label className="flex items-start gap-3 p-3 rounded-md border hairline cursor-pointer hover:border-[#4A5D4E]/50 bg-[#FDFCF8]" data-testid="doc-save-hide">
            <input type="checkbox" className="mt-0.5 accent-[#4A5D4E]" checked={hidden} onChange={(e) => setHidden(e.target.checked)} />
            <div className="text-xs leading-relaxed">
              <div className="text-[#1C1B1A] font-medium text-sm flex items-center gap-1.5"><EyeSlash size={14} weight="duotone" /> Hide this file after saving</div>
              <div className="text-[#6B6A68] mt-0.5">Kept in the library but excluded from the AI's answers until you show it again.</div>
            </div>
          </label>

          <div className="text-xs text-[#6B6A68] flex items-center justify-between">
            <span>Size: <span className="font-mono">{(content || "").length.toLocaleString()}</span> chars · {(bytes / 1000).toFixed(0)} KB</span>
            {oversize && <span className="text-[#C16E5A]" data-testid="doc-save-oversize">over the {perDocKB} KB Tier {tier} cap</span>}
          </div>
        </div>

        <div className="px-6 py-3 bg-[#FDFCF8] border-t hairline flex justify-end gap-2 shrink-0">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE] text-sm">Cancel</button>
          <button type="button" onClick={() => doSave()} disabled={busy || !title.trim()} data-testid="doc-save-submit"
            className="px-5 py-2 rounded-md bg-[#1C1B1A] text-white hover:bg-[#3A4A3E] text-sm disabled:opacity-50">
            {busy ? "Saving…" : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
